import {GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Row, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {TextPlus} from "web-vcore";
import {UpdateMapObject} from "../../../../../../Server/Commands/MapObjectCommands.js";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld} from "../../../../../../Server/DB/mapObjects/$mapObject.js";
import {GuideShape} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";
import {FbmNoiseLayer_Config, InheritPointsConfig, InterpolateConfig, ObjPath, PathTransform} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {CloneSafe} from "../../../../../../Utils/General/Serialization.js";
import {ShowPointsTransformDialog} from "../../@Shared/PointsTransformDialog.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";
import {GetPointsMatchingObjShape} from "../../Meshes_CompUI.js";
import {ShapeType} from "../../../../../../Server/DB/mapObjects/@MapObject.js";

export const PathEditorTab_Points = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {map, obj, path, index, Change} = props;
	const objPos = ResolveObjPosInWorld(obj);
	const allMapObjects = GetMapObjects(map.id);
	const pathIdDuplicate = !!path.path_id && (allMapObjects ?? []).some(obj2=>{
		return (obj2.paths ?? []).some(path2=>{
			if (obj2.id === obj.id && obj2.paths!.indexOf(path2) === index) return false; // skip self
			return path2.path_id === path.path_id;
		});
	});

	return <>
		<Row mt={5}>
			<TextPlus info={`
				When enabled, this path's points are derived from another path's points (by path_id) via a set of
				transforms, instead of using the literal points array below. The source path is searched for on this
				object first, then across all map objects. If the transforms array is empty, the source path's points
				are simply copied.
			`.AsMultiline(0)}>Inherit points:</TextPlus>
			<CheckBox ml={5} value={path.inheritPoints != null} onChange={val=>{
				Change(a=>{
					if (val) {
						a.inheritPoints = new InheritPointsConfig({source_pathID: ""});
						// Auto-generate a stable path_id on first enable (so other paths can derive from this one).
						if (!a.path_id) a.path_id = ObjPath.NewPathID();
					} else {
						a.inheritPoints = undefined;
					}
				});
			}}/>
		</Row>
		{path.inheritPoints && <>
			<Row mt={5}>
				<TextPlus info={`
					The path_id of the path to copy/transform points from. Searched on this object first, then across
					all map objects for the first path with this id.
				`.AsMultiline(0)}>Source path ID:</TextPlus>
				<TextInput ml={5} style={{flex: 1}} value={path.inheritPoints.source_pathID ?? ""} onChange={val=>Change(a=>a.inheritPoints!.source_pathID = val)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Use source path ID for seeding" value={path.inheritPoints.useSourcePathIDForSeeding} onChange={val=>Change(a=>a.inheritPoints!.useSourcePathIDForSeeding = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					When set (and > 0), the first N transforms defined on the SOURCE path are applied to the
					source's inherited base points before this path inherits them. Useful for reusing a source
					path's partial-transform pipeline (e.g. its curve interpolation) without re-applying the
					source's full transform stack. Leave at 0 to inherit the source's base unchanged (default).
				`.AsMultiline(0)}>Apply source transforms (first N):</TextPlus>
				<Spinner ml={5} min={0} step={1} value={path.inheritPoints.applySourceTransformCount ?? 0} onChange={val=>Change(a=>a.inheritPoints!.applySourceTransformCount = val)}/>
			</Row>
			<Row mt={5} style={{fontStyle: "italic"}}>Own-points hidden since inherit-points enabled.</Row>
		</>}
		{!path.inheritPoints && <>
			<Row mt={5}>
				<Text>Guide, shape:</Text>
				<Select ml={5} options={[{name: "None", value: null}, ...GetEntries(GuideShape, "ui")]} value={path.guide_shape} onChange={val=>Change(a=>a.guide_shape = val)}/>
				<Text ml={5}>Size:</Text>
				<Spinner ml={5} min={0} step={0.1} value={path.guide_size ?? 0} onChange={val=>Change(a=>a.guide_size = val)}/>
				<CheckBox ml={5} text="Align points" value={path.guide_alignPoints ?? false} onChange={val=>Change(a=>a.guide_alignPoints = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					Whether the path loops back on itself (closed) or is open (distinct endpoints).
					The single source of truth for path topology (used by both the curve interpolate
					transform and the terrain-feature rasterizer).
					Closed: the curve wraps back to its first point. Use for any loop (lakes, isolines, closed boundaries).
					Open: the curve has distinct endpoints. Use for rivers, roads, ridgelines.
				`.AsMultiline(0)}>Closed:</TextPlus>
				<CheckBox ml={5} value={path.curve_closed ?? false} onChange={val=>Change(a=>a.curve_closed = val)}/>
			</Row>
			<Row mt={5}>
				<Text>Points:</Text>
				<Button ml="auto" text="Transforms" onClick={()=>{
					ShowPointsTransformDialog(path.points);
				}}/>
				<Button ml={5} mdIcon="square-outline" title="Reset (to 1x1 square)" onClick={()=>{
					ShowMessageBox({
						title: "Reset points?", cancelButton: true,
						message: "Are you sure you want to reset the points to a simple horizontal line?",
						onOK: ()=>{
							const newPaths = CloneSafe(obj.paths);
							const squareSize = obj.paths[index].guide_size ?? 1;
							newPaths[index].points = [[0, 0], [squareSize, 0]];
							new UpdateMapObject({
								id: obj.id,
								updates: {paths: newPaths},
							}).RunOnServer();
						},
					});
				}}/>
				<Button ml={5} mdIcon="square" title="Reset to rect" onClick={()=>{
					ShowMessageBox({
						title: "Reset path to rect?", cancelButton: true,
						message: "Reset path to a rectangle, filling the shape-rect area?",
						onOK: ()=>{
							const newPaths = CloneSafe(obj.paths);
							newPaths[index].points = GetPointsMatchingObjShape(obj.shape_size, ShapeType.rectangle);
							new UpdateMapObject({
								id: obj.id,
								updates: {paths: newPaths},
							}).RunOnServer();
						},
					});
				}}/>
			</Row>
			<TextArea autoSize={true} value={JSON.stringify(path.points)} onChange={val=>Change(a=>a.points = JSON.parse(val))}/>
		</>}
		{/* One-time migration helper: detect stray pre-refactor `curveType` / `curve_*_config`
				JSON keys on this path (tolerated by the data pipeline but inert) and offer to
				convert them into an `interpolate` PathTransform entry, then delete the old fields. */}
		{(path as any).curveType != null && <Row mt={8}>
			<Button text="Convert old curve-type to transform" title={`This path carries pre-refactor curve-type fields (curveType="${(path as any).curveType}"${(path as any).curve_catmullRom_config != null ? " + curve_catmullRom_config" : ""}${(path as any).curve_fbmNoise_config != null ? " + curve_fbmNoise_config" : ""}) that are now inert. Click to add an equivalent interpolate transform and remove the old fields.`}
				onClick={()=>Change(a=>{
					const pa = a as any;
					const oldType = pa.curveType;
					const oldCatmull = pa.curve_catmullRom_config;
					const oldFbm = pa.curve_fbmNoise_config;
					// Flatten the old curve_catmullRom_config sub-fields onto InterpolateConfig
					// directly (subtype/tension/pointsPerSegment→outputSegmentRatio), and emit an
					// FbmNoiseLayer_Config for the fBm case. The catmullRom sub-config is gone as of
					// 2026-08-02 (InterpolateConfig now carries the CR params directly).
					const interp = new InterpolateConfig({
						subtype: oldCatmull?.subtype,
						tension: oldCatmull?.tension,
						outputSegmentRatio: oldCatmull?.pointsPerSegment,
					});
					if (oldType === "fbmNoise" || (oldFbm != null && oldType !== "catmullRom")) {
						interp.fbmNoise = new FbmNoiseLayer_Config(oldFbm ?? {});
					}
					a.transforms = (a.transforms ?? []).concat(new PathTransform({interpolate: interp}));
					delete pa.curveType;
					delete pa.curve_basic_config;
					delete pa.curve_catmullRom_config;
					delete pa.curve_fbmNoise_config;
				})}/>
		</Row>}
	</>;
});